import { autoinject } from "aurelia-dependency-injection";
import { action, observable } from "mobx";
import { ApiInstance, ApiService, defaultRequestTimeout, fetchWithTimeout, parseApiInstances } from "./ApiService";
import { OnlineService } from "./OnlineService";
import { SettingsService } from "./SettingsService";

/**
 * How many consecutive successful discovery responses may miss the selected
 * instance before the app drops back to its initial state. A short rollout
 * glitch on the discovery side must not de-register the whole fleet.
 */
const missingInstanceThreshold = 2;

/**
 * Keeps the api urls in sync with the urls discovery endpoint
 * (`window.apiDiscoveryUrl`), which answers with
 * `[{ name: "LIVE", urls: ["https://xxx/api-gatecheck/v1/", ...] }, ...]`.
 *
 * The instance name is the key: as long as the selected name is offered, a
 * changed url list is applied silently in the background and every following
 * api call uses it. When the name disappears, the selection is lost and the
 * user has to pick an instance again.
 */
@autoinject()
export class ApiDiscoveryService {
    constructor(
        private apiService: ApiService,
        private settingsService: SettingsService,
        private onlineService: OnlineService) {
    }

    /** Instances offered by the discovery endpoint, for the selection UI. */
    @observable instances: ApiInstance[] = [];
    @observable lastSuccess: Date = null;
    @observable lastError: string = null;

    /** Called when the selected instance is not offered any more. */
    onInstanceLost: (instanceName: string) => Promise<any>;

    private intervalHandle: number = null;
    private missingCount: number = 0;

    get isEnabled(): boolean {
        return this.apiService.isDiscoveryEnabled;
    }

    @action start() {
        this.stop();
        if (!this.isEnabled) {
            return;
        }

        this.refresh();
        this.intervalHandle = setInterval(() => this.refresh(), this.settingsService.apiDiscoveryInterval);
    }

    @action stop() {
        if (this.intervalHandle !== null) {
            clearInterval(this.intervalHandle);
            this.intervalHandle = null;
        }
    }

    async refresh(): Promise<boolean> {
        if (!this.isEnabled || !this.onlineService.isOnline) {
            return false;
        }

        try {
            const instances = await this.download();
            // An empty or unreadable answer is never treated as "everything is
            // gone" - the last known good list keeps being used.
            if (instances.length === 0) {
                throw new Error("no valid instance returned");
            }
            this.apply(instances);
            return true;
        } catch (e) {
            this.setError(e && e.message ? e.message : `${e}`);
            return false;
        }
    }

    private async download(): Promise<ApiInstance[]> {
        const response = await fetchWithTimeout(this.apiService.discoveryUrl, {
            method: "GET",
            headers: { "Accept": "application/json" },
            cache: "no-store",
        }, defaultRequestTimeout);

        if (!response.ok) {
            throw new Error(`discovery response ${response.status}`);
        }
        return parseApiInstances(await response.json());
    }

    @action private apply(instances: ApiInstance[]) {
        this.instances = instances;
        this.lastSuccess = new Date();
        this.lastError = null;

        const selectedName = this.settingsService.apiInstanceName;
        if (!selectedName) {
            this.missingCount = 0;
            this.adopt(instances);
            return;
        }

        const selected = instances.find(i => i.name === selectedName);
        if (selected) {
            this.missingCount = 0;
            this.applyUrls(selected);
            return;
        }

        this.missingCount++;
        console.warn(`[ApiDiscoveryService] instance "${selectedName}" is missing in discovery (${this.missingCount}/${missingInstanceThreshold})`);
        if (this.missingCount >= missingInstanceThreshold) {
            this.missingCount = 0;
            if (this.onInstanceLost) {
                this.onInstanceLost(selectedName);
            }
        }
    }

    /**
     * A device registered before the discovery endpoint was deployed knows only
     * its single url. When exactly one instance offers exactly that url, the
     * device is bound to it and takes part in the url updates and the failover
     * from now on - without a re-registration.
     */
    private adopt(instances: ApiInstance[]) {
        const currentRoot = this.apiService.apiRoot;
        if (!currentRoot || !this.apiService.isInitialized) {
            return;
        }

        const matches = instances.filter(i => i.urls.indexOf(currentRoot) >= 0);
        if (matches.length !== 1) {
            return;
        }

        console.log(`[ApiDiscoveryService] "${currentRoot}" adopted by instance "${matches[0].name}"`);
        this.applySelection(matches[0]);
    }

    private applyUrls(instance: ApiInstance) {
        const current = this.apiService.apiRoots.slice();
        const isSame = current.length === instance.urls.length
            && current.every((url, index) => url === instance.urls[index]);
        if (isSame) {
            return;
        }

        console.log(`[ApiDiscoveryService] urls of "${instance.name}" updated: ${instance.urls}`);
        this.applySelection(instance);
    }

    private applySelection(instance: ApiInstance) {
        this.settingsService.update(s => {
            s.apiInstanceName = instance.name;
            s.apiRoots = instance.urls.slice();
            s.apiRoot = instance.urls[0];
        });
    }

    @action private setError(message: string) {
        this.lastError = message;
        console.warn(`[ApiDiscoveryService] discovery failed: ${message}`);
    }
}
